import {FormControl, FormField} from 'platform/components';
import {Heading, Show, Text, VStack} from 'platform/foundation';

import {FieldPathByValue, FieldValues} from 'react-hook-form';

import {CampaignFilter} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleSelectionFormBinding} from '../../types/VehicleSelectionFormBinding';
import {VehicleSelectionMode} from '../../types/VehicleSelectionMode';
import {VehicleSelectionFilterFields} from '../VehicleSelectionFilterFields/VehicleSelectionFilterFields';

const VEHICLE_SELECTION_MODE_OPTIONS: {value: VehicleSelectionMode; label: string}[] = [
  {
    value: VehicleSelectionMode.ALL_VEHICLES,
    label: i18n.t('entity.salesCampaign.membershipType.allVehicles'),
  },
  {
    value: VehicleSelectionMode.BY_CONDITIONS,
    label: i18n.t('entity.promoPhotos.vehicleSelection.labels.byConditions'),
  },
];

interface VehicleSelectionSectionProps<
  TFieldValues extends FieldValues,
> extends RequiredTestIdProps {
  control: FormControl<TFieldValues>;
  binding: VehicleSelectionFormBinding<TFieldValues>;
  modeFieldName: FieldPathByValue<TFieldValues, VehicleSelectionMode>;
  // Both watched by the owning form.
  vehicleSelectionMode: VehicleSelectionMode;
  filter: CampaignFilter | Nullish;
  // Names what the matching vehicles get (the photo, the watermark).
  conditionsDescription: string;
}

// "Apply to vehicles" of a REGULAR promotional photo or a watermark: every vehicle in sale, or
// only the ones matching the conditions. The conditions stay in the form while "all vehicles" is
// chosen, so switching back restores them; only "by conditions" sends them.
export function VehicleSelectionSection<TFieldValues extends FieldValues>(
  props: VehicleSelectionSectionProps<TFieldValues>
) {
  return (
    <VStack spacing={4}>
      <FormField
        control={props.control}
        type="radio"
        name={props.modeFieldName}
        label={i18n.t('entity.promoPhotos.vehicleSelection.labels.applyToVehicles')}
        options={VEHICLE_SELECTION_MODE_OPTIONS}
        data-testid={suffixTestId('mode', props)}
      />
      <Show when={props.vehicleSelectionMode === VehicleSelectionMode.BY_CONDITIONS}>
        <VStack spacing={1}>
          <Heading size={5}>{i18n.t('entity.interest.labels.conditions')}</Heading>
          <Text size="xSmall" color="tertiary">
            {props.conditionsDescription}
          </Text>
        </VStack>
        <VehicleSelectionFilterFields
          control={props.control}
          binding={props.binding}
          filter={props.filter}
          areExtendedOperatorsEnabled
          data-testid={suffixTestId('filter', props)}
        />
      </Show>
    </VStack>
  );
}
